import { HistoryStack } from '../../utils/history';

type HistoryChangeCallback = (canUndo: boolean, canRedo: boolean) => void;

interface UnifiedHistoryContext {
  getSnapshot: () => string;
  restoreSnapshot: (snapshot: string) => void;
  onChange: () => void;
}

export class UnifiedHistoryController {
  private readonly history = new HistoryStack<string>({
    maxLength: 50,
    minUndoDepth: 1,
    useStackCurrent: true,
    isEqual: (a: string, b: string): boolean => a === b,
  });
  private readonly getSnapshot: () => string;
  private readonly restoreSnapshot: (snapshot: string) => void;
  private readonly onChange: () => void;
  private onHistoryChange: HistoryChangeCallback | null = null;
  private isRestoring = false;

  constructor(context: UnifiedHistoryContext) {
    this.getSnapshot = context.getSnapshot;
    this.restoreSnapshot = context.restoreSnapshot;
    this.onChange = context.onChange;
  }

  setOnHistoryChange(callback: HistoryChangeCallback): void {
    this.onHistoryChange = callback;
  }

  recordSnapshot(): void {
    if (this.isRestoring) return;
    this.history.record(this.getSnapshot());
    this.notifyHistoryChange();
  }

  ensureSnapshot(snapshot: string): void {
    this.history.ensureSnapshot(snapshot);
  }

  load(undoStack: string[] = [], redoStack: string[] = []): void {
    this.history.load(undoStack, redoStack);
  }

  getUndoStack(): string[] {
    return this.history.getUndoStack();
  }

  getRedoStack(): string[] {
    return this.history.getRedoStack();
  }

  undo(): void {
    const previousState = this.history.undo();
    if (!previousState) return;
    this.isRestoring = true;
    this.restoreSnapshot(previousState);
    this.isRestoring = false;
    this.notifyHistoryChange();
    this.onChange();
  }

  redo(): void {
    const nextState = this.history.redo();
    if (!nextState) return;
    this.isRestoring = true;
    this.restoreSnapshot(nextState);
    this.isRestoring = false;
    this.notifyHistoryChange();
    this.onChange();
  }

  canUndo(): boolean {
    return this.history.canUndo();
  }

  canRedo(): boolean {
    return this.history.canRedo();
  }

  notifyHistoryChange(): void {
    this.onHistoryChange?.(this.canUndo(), this.canRedo());
  }
}
